import { useState, useEffect, useCallback } from 'react';
import { ordersApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ShoppingBag, Phone, Mail, Truck, Store, ChevronDown, ChevronUp, Trash2, CalendarDays, FileText, Plus } from 'lucide-react';
import { InvoicePanel } from './orders/InvoicePanel';
import { ManualOrderDialog } from './orders/ManualOrderDialog';

const STATUSES = ['new', 'confirmed', 'scheduled', 'delivered', 'cancelled'];
const STATUS_STYLE = {
  new: 'bg-orange-100 text-orange-800',
  confirmed: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
};
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

function OrderCard({ order, onUpdate, onDelete }) {
  const [open, setOpen] = useState(order.status === 'new');
  const [notes, setNotes] = useState(order.admin_notes || '');
  const [fee, setFee] = useState(order.delivery_fee == null ? '' : String(order.delivery_fee));
  const [scheduled, setScheduled] = useState(order.scheduled_date || order.preferred_date || '');
  const quotePending = order.delivery_method === 'delivery' && order.delivery_fee == null;
  const onCalendar = ['confirmed', 'scheduled', 'delivered'].includes(order.status);

  const changeStatus = async (status) => {
    try { await ordersApi.updateStatus(order.id, { status }); toast.success(`Order marked ${status}`); onUpdate(); }
    catch { toast.error('Update failed'); }
  };
  const saveScheduled = async () => {
    try { await ordersApi.updateStatus(order.id, { status: order.status === 'new' || order.status === 'confirmed' ? 'scheduled' : order.status, scheduled_date: scheduled }); toast.success(scheduled ? 'Delivery date set — now on the farm calendar' : 'Delivery date cleared'); onUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not save date'); }
  };
  const saveNotes = async () => {
    try { await ordersApi.updateStatus(order.id, { status: order.status, admin_notes: notes }); toast.success('Notes saved'); onUpdate(); }
    catch { toast.error('Could not save notes'); }
  };
  const saveFee = async () => {
    const value = parseFloat(fee);
    if (Number.isNaN(value) || value < 0) { toast.error('Enter a valid delivery fee'); return; }
    try { await ordersApi.updateStatus(order.id, { status: order.status, delivery_fee: value }); toast.success('Delivery fee set'); onUpdate(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not save fee'); }
  };

  return (
    <Card data-testid={`order-card-${order.order_number}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => setOpen(!open)} className="flex items-center gap-2 font-heading font-bold text-lg" data-testid={`order-toggle-${order.order_number}`}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {order.order_number}
          </button>
          <Badge className={STATUS_STYLE[order.status]} data-testid={`order-status-badge-${order.order_number}`}>{order.status}</Badge>
          {quotePending && <Badge className="bg-yellow-100 text-yellow-800" data-testid={`quote-pending-${order.order_number}`}>Delivery quote needed</Badge>}
          {order.scheduled_date && <Badge variant="outline" className="gap-1" data-testid={`scheduled-badge-${order.order_number}`}><CalendarDays className="h-3 w-3" /> {order.scheduled_date}</Badge>}
          {order.invoice && (
            <Badge className={`gap-1 ${order.invoice.payment_status === 'paid' ? 'bg-green-100 text-green-800' : order.invoice.payment_status === 'void' ? 'bg-gray-200 text-gray-700 line-through' : 'bg-orange-100 text-orange-800'}`} data-testid={`invoice-badge-${order.order_number}`}>
              <FileText className="h-3 w-3" /> {order.invoice.number} · {order.invoice.payment_status === 'paid' ? 'Paid' : order.invoice.payment_status === 'void' ? 'Void' : 'Unpaid'}
            </Badge>
          )}
          {order.invoice?.payment_status === 'void' && order.status !== 'cancelled' && (
            <Badge className="bg-yellow-100 text-yellow-800" data-testid={`reinvoice-needed-${order.order_number}`}>New invoice needed</Badge>
          )}
          <span className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString('en-AU')}</span>
          <span className="font-medium">{order.customer_name}</span>
          {order.source === 'phone' && <Badge variant="outline" className="gap-1 text-xs" data-testid={`source-badge-${order.order_number}`}><Phone className="h-3 w-3" /> Phone order</Badge>}
          <span className="ml-auto font-heading font-bold text-primary" data-testid={`order-total-${order.order_number}`}>{order.total_m2} m² · {fmt(order.total)}</span>
          <Select value={order.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-36" data-testid={`order-status-select-${order.order_number}`}><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {open && (
          <div className="mt-5 grid md:grid-cols-2 gap-6 text-sm" data-testid={`order-details-${order.order_number}`}>
            <div className="space-y-2">
              <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> <a href={`tel:${order.phone}`} className="hover:underline">{order.phone}</a></p>
              <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {order.email ? <a href={`mailto:${order.email}`} className="hover:underline">{order.email}</a> : <span className="text-muted-foreground italic">No email</span>}
                {order.notifications?.customer && <span className={`text-xs px-1.5 py-0.5 rounded ${order.notifications.customer === 'sent' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-testid={`confirmation-status-${order.order_number}`}>{order.notifications.customer === 'sent' ? 'Confirmation sent' : order.notifications.customer === 'not_configured' ? 'Confirmation not sent (email not set up)' : `Confirmation ${order.notifications.customer}`}</span>}
              </p>
              <p className="flex items-start gap-2">
                {order.delivery_method === 'pickup' ? <Store className="h-4 w-4 text-muted-foreground mt-0.5" /> : <Truck className="h-4 w-4 text-muted-foreground mt-0.5" />}
                <span>{order.delivery_method === 'pickup' ? 'Farm pickup' : <><span className="font-medium">Deliver to:</span> {[order.address, order.suburb, order.postcode].filter(Boolean).join(', ')}</>}</span>
              </p>
              {order.preferred_date && <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-muted-foreground" /> Preferred: {order.preferred_date}</p>}
              {order.notes && <p className="p-3 bg-secondary/30 rounded-md"><span className="font-medium">Customer notes:</span> {order.notes}</p>}
              <ul className="divide-y border rounded-md mt-3">
                {order.items.map((i) => (
                  <li key={i.variety_id} className="flex justify-between px-3 py-2"><span>{i.variety_name} × {i.quantity_m2} m²</span><span className="font-medium">{fmt(i.line_total)}</span></li>
                ))}
                <li className="flex justify-between px-3 py-2 text-muted-foreground"><span>Turf subtotal</span><span>{fmt(order.subtotal)}</span></li>
                <li className="flex justify-between px-3 py-2 text-muted-foreground">
                  <span>{order.delivery_method === 'pickup' ? 'Pickup' : `Delivery${order.delivery_zone ? ` · ${order.delivery_zone}` : ''}`}</span>
                  <span data-testid={`order-delivery-fee-${order.order_number}`}>{order.delivery_method === 'pickup' ? 'Free' : order.delivery_fee == null ? 'Not quoted' : fmt(order.delivery_fee)}</span>
                </li>
                <li className="flex justify-between px-3 py-2 font-bold bg-secondary/30"><span>Total</span><span>{fmt(order.total)}</span></li>
              </ul>
            </div>
            <div className="space-y-2">
              <div className={`p-3 rounded-md border ${onCalendar && order.scheduled_date ? 'bg-purple-50 border-purple-200' : 'bg-secondary/20'}`} data-testid={`schedule-editor-${order.order_number}`}>
                <p className="font-medium mb-2 flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {order.delivery_method === 'pickup' ? 'Pickup date' : 'Delivery date'}</p>
                <div className="flex gap-2">
                  <Input type="date" value={scheduled} onChange={(e) => setScheduled(e.target.value)} className="w-44" data-testid={`schedule-input-${order.order_number}`} />
                  <Button size="sm" onClick={saveScheduled} data-testid={`save-schedule-${order.order_number}`}>{order.status === 'new' || order.status === 'confirmed' ? 'Schedule' : 'Save date'}</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {order.scheduled_date
                    ? (onCalendar ? 'Shows on the farm calendar with the cut list for that day.' : 'Date saved — appears on the calendar once the order is confirmed or scheduled.')
                    : order.preferred_date
                      ? `Customer asked for ${order.preferred_date}. ${onCalendar ? 'Showing on the calendar as a preferred date until you schedule it.' : 'Confirm or schedule to put it on the calendar.'}`
                      : 'No date yet — set one so the crew sees it on the calendar.'}
                </p>
              </div>
              {order.delivery_method === 'delivery' && (
                <div className={`p-3 rounded-md border ${quotePending ? 'bg-yellow-50 border-yellow-200' : 'bg-secondary/20'}`} data-testid={`fee-editor-${order.order_number}`}>
                  <p className="font-medium mb-2">{quotePending ? 'Set delivery fee for this order' : 'Delivery fee'}</p>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="0" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="e.g. 95" className="w-32" data-testid={`fee-input-${order.order_number}`} />
                    <Button size="sm" onClick={saveFee} data-testid={`save-fee-${order.order_number}`}>Save fee</Button>
                  </div>
                  {quotePending && <p className="text-xs text-muted-foreground mt-2">{order.suburb}{order.postcode ? ` ${order.postcode}` : ''} isn't in a delivery zone. The customer will see this fee on their order tracking page.</p>}
                </div>
              )}
              <InvoicePanel order={order} onUpdate={onUpdate} />
              <p className="font-medium">Internal notes</p>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Delivery quote, call outcome, cut date…" data-testid={`order-notes-${order.order_number}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveNotes} data-testid={`save-notes-${order.order_number}`}>Save notes</Button>
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive ml-auto" onClick={() => onDelete(order)} data-testid={`delete-order-${order.order_number}`}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ by_status: {} });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, s] = await Promise.all([ordersApi.getAll(filter === 'all' ? undefined : filter), ordersApi.getSummary()]);
      setOrders(o.data); setSummary(s.data);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete order ${order.order_number}?`)) return;
    try { await ordersApi.delete(order.id); toast.success('Order deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3"><ShoppingBag className="h-8 w-8 text-primary" /> Customer Orders</h1>
          <p className="text-muted-foreground">Orders from your website and phone orders entered by staff</p>
        </div>
        <Button className="btn-primary" onClick={() => setManualOpen(true)} data-testid="new-phone-order-btn"><Plus className="h-4 w-4 mr-2" /> New phone order</Button>
      </div>

      <ManualOrderDialog open={manualOpen} onOpenChange={setManualOpen} onCreated={load} />

      <div className="flex flex-wrap gap-2" data-testid="order-filters">
        {['all', ...STATUSES].map((s) => {
          const count = s === 'all' ? Object.values(summary.by_status).reduce((a, b) => a + b.count, 0) : summary.by_status[s]?.count || 0;
          return (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize border transition-colors ${filter === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-secondary'}`} data-testid={`filter-${s}`}>
              {s} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? <div className="animate-pulse h-64 bg-muted rounded-md" /> : orders.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="no-orders"><ShoppingBag className="h-10 w-10 mx-auto mb-3 text-primary" />No {filter === 'all' ? '' : filter} orders yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">{orders.map((o) => <OrderCard key={o.id} order={o} onUpdate={load} onDelete={handleDelete} />)}</div>
      )}
    </div>
  );
}
